import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-[6px] border border-transparent bg-clip-padding font-ibm-plex text-sm font-medium uppercase tracking-wide whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&>span]:font-inherit [&>span]:uppercase [&>span]:tracking-wide [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a]:hover:bg-primary/80",
        outline:
          "border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        /** Tropical Surf stroke + label + icons (brand primary outline). */
        "outline-brand":
          "border-[color:var(--brand-tropical-surf)] bg-background text-[color:var(--brand-tropical-surf)] hover:bg-[color:var(--brand-tropical-surf)]/10 hover:text-[color:var(--brand-tropical-surf)] focus-visible:border-[color:var(--brand-tropical-surf)] focus-visible:ring-[color:var(--brand-tropical-surf)]/25 [&_svg]:text-current",
        /** Glacial orange stroke + label (archive actions). */
        "outline-orange":
          "border-[color:var(--brand-comparison-period)] bg-background text-[color:var(--brand-comparison-period)] hover:bg-[color:var(--brand-comparison-period)]/10 hover:text-[color:var(--brand-comparison-period)] focus-visible:border-[color:var(--brand-comparison-period)] focus-visible:ring-[color:var(--brand-comparison-period)]/25 [&_svg]:text-current",
        /** Green stroke + label (current policy version). */
        "outline-green":
          "border-green-500 bg-background text-green-500 hover:bg-green-500/10 hover:text-green-500 focus-visible:border-green-500 focus-visible:ring-green-500/25 [&_svg]:text-current dark:border-green-500 dark:text-green-500",
        /** Gray-500 stroke + label (secondary open actions). */
        "outline-gray":
          "border-gray-500 bg-transparent text-gray-500 hover:bg-gray-500/10 hover:text-gray-500 focus-visible:border-gray-500 focus-visible:ring-gray-500/25 [&_svg]:text-current dark:border-white dark:text-white dark:hover:border-white dark:hover:text-white dark:hover:bg-transparent",
        /** Destructive outline — same red as active-pine delete; no fill on hover. */
        "outline-red":
          "border-[#e00101] bg-transparent text-[#e00101] hover:bg-transparent hover:text-[#e00101] focus-visible:border-[#e00101] focus-visible:ring-[#e00101]/25 [&_svg]:text-current dark:border-[#e00101] dark:text-[#e00101] dark:hover:bg-transparent",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        destructive:
          "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
        link: "text-primary underline-offset-4 hover:underline",
        gradient:
          "border-0 bg-transparent bg-[image:var(--gradient-primary)] text-white shadow-none hover:opacity-90 active:opacity-95 focus-visible:ring-white/30 [&_svg]:text-white",
        "gradient-orange":
          "border-0 bg-transparent bg-[image:var(--gradient-glacial-orange)] text-white shadow-none hover:opacity-90 active:opacity-95 focus-visible:ring-white/30 [&_svg]:text-white",
        "gradient-red":
          "border-0 bg-transparent bg-[image:var(--gradient-glacial-red)] text-white shadow-none hover:opacity-90 active:opacity-95 focus-visible:ring-white/30 [&_svg]:text-white",
        /** Glacial green fill (current policy version status). */
        "gradient-green":
          "border-0 bg-transparent bg-[image:var(--gradient-glacial-green)] text-white shadow-none hover:opacity-90 active:opacity-95 focus-visible:ring-white/30 [&_svg]:text-white",
      },
      size: {
        default:
          "h-9 gap-1.5 px-3 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xs: "h-7 gap-1 px-2 text-xs in-data-[slot=button-group]:focus-visible:!ring-0 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-9 gap-1.5 px-3 text-[0.8rem] in-data-[slot=button-group]:focus-visible:!ring-0 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-10 gap-1.5 px-3 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        icon: "size-9",
        "icon-xs":
          "size-7 in-data-[slot=button-group]:focus-visible:!ring-0 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-9 in-data-[slot=button-group]:focus-visible:!ring-0",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    "data-font"?: "primary" | (string & {})
  }

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      className,
      variant = "default",
      size = "default",
      asChild = false,
      style,
      "data-font": dataFont,
      ...props
    },
    ref
  ) {
    const Comp = asChild ? Slot.Root : "button"
    const usesPrimaryFont = dataFont === "primary"
    const fallbackStyle: React.CSSProperties = {
      ...(size === "default" ? { height: "2.25rem", paddingInline: "0.75rem" } : {}),
      ...(size === "sm" ? { height: "2.25rem", paddingInline: "0.75rem" } : {}),
      ...(usesPrimaryFont
        ? {
            fontFamily: "var(--font-primary)",
            fontSize: "0.875rem",
            fontWeight: 400,
            textTransform: "none",
            letterSpacing: "normal",
          }
        : {}),
      ...(variant === "outline"
        ? {
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: "var(--border)",
            backgroundColor: "var(--background)",
            color: "var(--foreground)",
          }
        : {}),
      ...(variant === "outline-brand"
        ? {
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: "var(--brand-tropical-surf)",
            backgroundColor: "transparent",
            color: "var(--brand-tropical-surf)",
          }
        : {}),
      ...(variant === "outline-orange"
        ? {
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: "var(--brand-comparison-period)",
            backgroundColor: "transparent",
            color: "var(--brand-comparison-period)",
          }
        : {}),
      ...(variant === "outline-green"
        ? {
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: "#22c55e",
            backgroundColor: "transparent",
            color: "#22c55e",
          }
        : {}),
      ...(variant === "outline-gray"
        ? {
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: "#6b7280",
            backgroundColor: "transparent",
            color: "#6b7280",
          }
        : {}),
      ...(variant === "outline-red"
        ? {
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: "#e00101",
            backgroundColor: "transparent",
            color: "#e00101",
          }
        : {}),
      ...(variant === "gradient" ||
      variant === "gradient-orange" ||
      variant === "gradient-red" ||
      variant === "gradient-green"
        ? {
            borderWidth: 0,
            borderStyle: "solid",
            backgroundColor: "transparent",
            backgroundImage:
              variant === "gradient-orange"
                ? "var(--gradient-glacial-orange)"
                : variant === "gradient-red"
                  ? "var(--gradient-glacial-red)"
                  : variant === "gradient-green"
                    ? "var(--gradient-glacial-green)"
                    : "var(--gradient-primary)",
            color: "#ffffff",
          }
        : {}),
    }

    return (
      <Comp
        ref={ref}
        data-slot="button"
        data-variant={variant}
        data-size={size}
        className={cn(
          buttonVariants({ variant, size }),
          usesPrimaryFont && "!text-[0.875rem] [&>span]:!text-[0.875rem]",
          className
        )}
        style={{ ...fallbackStyle, ...style }}
        data-font={dataFont}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
